<template>
    <hox-modal @close="close">
        <hox-loading-layer v-if="isAddingFont" />
        <template #header>Add custom font</template>
        <FontForm
            :upload-config="uploadConfig"
            @validationErrorsUpdate="setValidationErrors"
            @formDataUpdate="setFormData"
            @formTabSwitched="onTabChange"
            @submit="addFont"
        />
        <Alert banner type="warning">
            Licensing
            <template #desc>Only upload fonts if the appropriate font license agreement has been obtained</template>
        </Alert>
        <div v-if="error" class="add-font-modal__unexpected-error-wrapper">
            <Alert banner type="error">
                <template #desc>{{ error }}</template>
            </Alert>
        </div>
        <template #footer>
            <Button :disabled="isAddingFont" type="primary" data-testid="add-font-modal__add-button" @click="addFont">
                Add
            </Button>
        </template>
    </hox-modal>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import { snackbar } from "@/utils";
import addFontMutation from "@/apollo/mutations/AddFont.gql";
import FontForm, { type FontFormData } from "@/components/FontForm.vue";
import { apolloClient } from "@/apollo";
import type { Err } from "@/types/types";

const { uploadConfig } = defineProps({
    uploadConfig: {
        required: true,
        type: Object
    }
});

const emit = defineEmits(["close", "fontAdded"]);

const route = useRoute();

const error = ref("");
const isAddingFont = ref(false);
const formData = ref<FontFormData>({});
const validationErrors = ref<string[]>([]);

const clientId = computed(() => route.params.clientId);

async function addFont() {
    if (!isAddingFont.value) {
        if (validationErrors.value.length) {
            const [firstError] = validationErrors.value;
            error.value = firstError;
            return;
        }

        isAddingFont.value = true;
        error.value = "";

        try {
            await apolloClient.mutate({
                mutation: addFontMutation,
                variables: {
                    clientId: clientId.value,
                    font: {
                        name: formData.value.name,
                        ...(formData.value.key && { key: formData.value.key }),
                        ...(formData.value.url && { url: formData.value.url })
                    }
                }
            });

            snackbar.success("Font added");
            emit("fontAdded");
            close();
        } catch (err: unknown) {
            const graphQLErrors = (err as Err).graphQLErrors || [];
            const duplicateKey = graphQLErrors.find(
                ({ extensions }) => extensions && extensions.code === "DUPLICATE_KEY"
            );

            error.value = duplicateKey
                ? duplicateKey.message
                : "There was an unexpected error and the font could not be added. Please try again in a few moments.";
        } finally {
            isAddingFont.value = false;
        }
    }
}

function close() {
    emit("close");
}

function onTabChange() {
    error.value = "";
}

function setFormData(newFormData: FontFormData) {
    formData.value = newFormData;
}

function setValidationErrors(newValidationErrors: string[]) {
    validationErrors.value = newValidationErrors;

    if (!validationErrors.value.length) {
        error.value = "";
    }
}
</script>

<style lang="scss">
@use "@style/variables" as *;

.add-font-modal__unexpected-error-wrapper {
    padding: $spacing 0 0 0;
}
</style>
